/**
 * Canonical PostHog-style marketing event names + typed payloads for consent-gated tracking.
 *
 * **Naming schema**
 * - Marketing / landing: `landing_{surface}_{object}_{action}` (snake_case verbs, past tense where natural).
 * - Cross-site navigation: `site_{channel}_{action}`.
 * - Properties: snake_case; include stable ids (`pillar_id`, `topic_key`, `tab_key`) plus human-readable
 *   `*_title` slugs (`question_title`, `screenshot_title`, `feature_title`) alongside indexes for analysis.
 */

export const MARKETING_EVENTS = {
	landingSectionViewed: "landing_section_viewed",
	landingCtaClicked: "landing_cta_clicked",
	landingFaqExpanded: "landing_faq_expanded",
	landingSolutionDemoFeatureSelected:
		"landing_solution_demo_feature_selected",
	landingSolutionDemoPathsRefreshed: "landing_solution_demo_paths_refreshed",
	landingSolutionDemoRegenerateClicked:
		"landing_solution_demo_regenerate_clicked",
	landingSolutionDemoRestartClicked: "landing_solution_demo_restart_clicked",
	landingSolutionDemoInteractableOpened:
		"landing_solution_demo_interactable_opened",
	landingSolutionDemoSessionStarted: "landing_solution_demo_session_started",
	landingSolutionDemoTopicCompleted: "landing_solution_demo_topic_completed",
	landingSolutionDemoLevelReached: "landing_solution_demo_level_reached",
	landingSolutionDemoSectionCompleted:
		"landing_solution_demo_section_completed",
	landingSolutionDemoAllSectionsCompleted:
		"landing_solution_demo_all_sections_completed",
	landingSolutionDemoSoonOverlayViewed:
		"landing_solution_demo_soon_overlay_viewed",
	landingBackToTopClicked: "landing_back_to_top_clicked",
	landingColorModeChanged: "landing_color_mode_changed",
	landingFooterExternalClicked: "landing_footer_external_clicked",
	landingLaunchCountdownAvailabilityClicked:
		"landing_launch_countdown_availability_clicked",

	landingVideoPlayStarted: "landing_video_play_started",
	landingVideoProgress: "landing_video_progress",
	landingVideoMuted: "landing_video_muted",
	landingVideoUnmuted: "landing_video_unmuted",
	landingVideoFullscreenEntered: "landing_video_fullscreen_entered",
	landingVideoFullscreenExited: "landing_video_fullscreen_exited",

	landingFeaturesTabSelected: "landing_features_tab_selected",
	landingFeaturesLightboxOpened: "landing_features_lightbox_opened",
	landingFeaturesLightboxNavigated: "landing_features_lightbox_navigated",
	landingFeaturesScreenshotNavigated: "landing_features_screenshot_navigated",

	siteMailtoLinkClicked: "site_mailto_link_clicked",
	siteExternalLinkClicked: "site_external_link_clicked",

	notFoundViewed: "not_found_viewed",

	consentChoiceMade: "consent_choice_made",

	waitlistSubscribeOutcome: "waitlist_subscribe_outcome",
	waitlistVerificationResendOutcome: "waitlist_verification_resend_outcome",
	waitlistVerificationPollResolved: "waitlist_verification_poll_resolved",
	waitlistConfirmOutcome: "waitlist_confirm_outcome",
	waitlistUnsubscribeOutcome: "waitlist_unsubscribe_outcome",
	waitlistResubscribeOutcome: "waitlist_resubscribe_outcome",

	newsletterSubscribeOutcome: "newsletter_subscribe_outcome",
	newsletterVerificationResendOutcome:
		"newsletter_verification_resend_outcome",
	newsletterVerificationPollResolved: "newsletter_verification_poll_resolved",
	newsletterConfirmOutcome: "newsletter_confirm_outcome",
	newsletterUnsubscribeOutcome: "newsletter_unsubscribe_outcome",
	newsletterResubscribeOutcome: "newsletter_resubscribe_outcome",

	pricingBandChanged: "pricing_band_changed",
	pricingSeatsChanged: "pricing_seats_changed",
	pricingIntervalChanged: "pricing_interval_changed",
	pricingDisplayModeChanged: "pricing_display_mode_changed",
	pricingSelectionsCleared: "pricing_selections_cleared",
	pricingPlanSelected: "pricing_plan_selected",
	pricingContentTabChanged: "pricing_content_tab_changed",
	pricingFaqExpanded: "pricing_faq_expanded",
	pricingCtaClicked: "pricing_cta_clicked",
	pricingConsumableChanged: "pricing_consumable_changed",
	pricingFreeGated: "pricing_free_gated",
	pricingQuotePreviewShown: "pricing_quote_preview_shown",
	pricingCheckoutStarted: "pricing_checkout_started",
	pricingCheckoutRejected: "pricing_checkout_rejected",
	checkoutGateOpened: "checkout_gate_opened",
	checkoutConfirmStarted: "checkout_confirm_started",
	checkoutConfirmSucceeded: "checkout_confirm_succeeded",
	checkoutConfirmFailed: "checkout_confirm_failed",
	checkoutSeatsChanged: "checkout_seats_changed",
	checkoutSuccessPageViewed: "checkout_success_page_viewed",
	checkoutCanceledConfirmed: "checkout_canceled_confirmed",
	billingCancelAtPeriodEndConfirmed: "billing_cancel_at_period_end_confirmed",
	billingUncancelAtPeriodEndConfirmed:
		"billing_uncancel_at_period_end_confirmed",
	billingDeferredChangeCanceledConfirmed:
		"billing_deferred_change_canceled_confirmed",
	billingCollectionPreferenceChangedConfirmed:
		"billing_collection_preference_changed_confirmed",
	checkoutProvisioningReadyConfirmed: "checkout_provisioning_ready_confirmed",
	quoteComposed: "quote_composed",
	quoteFinalized: "quote_finalized",
	quoteIssued: "quote_issued",
	quoteIssueEmailSent: "quote_issue_email_sent",
	quoteLinkOpened: "quote_link_opened",
	quoteClaimStarted: "quote_claim_started",
	quoteClaimRedeemed: "quote_claim_redeemed",
	quoteRevealed: "quote_revealed",
	quoteAuthMismatch: "quote_auth_mismatch",
	quoteBillingPermissionDenied: "quote_billing_permission_denied",
	checkoutQuoteViewed: "checkout_quote_viewed",
	checkoutQuoteAbandoned: "checkout_quote_abandoned",
	checkoutQuoteResumed: "checkout_quote_resumed",
	checkoutQuotePaid: "checkout_quote_paid",
	quoteRevoked: "quote_revoked",
	quoteSuperseded: "quote_superseded",
	quoteReminderSent: "quote_reminder_sent",
	quoteReminderSkipped: "quote_reminder_skipped",
} as const;

export type MarketingEventName =
	(typeof MARKETING_EVENTS)[keyof typeof MARKETING_EVENTS];
